/*
    This file is part of Universal Sequencer.
    Universal Sequencer is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequencer is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/


import React, { Component } from 'react';
import { Group, Stage, Layer, Rect, Line} from 'react-konva';
import WaveformSelection from './WaveformSelection.js';
import WaveformPlayTick from './WaveformPlayTick.js';
import { ReactReduxContext, Provider } from 'react-redux';
import ConditionedComponent from 'components/ConditionedComponent.js';
import { connect } from "react-redux";
import {dragSample} from 'actions/index.js';
import Colors from 'components/Colors.js';
import {convertPointsToList} from './waveform/convertPointsToList.js';
import {setContextualHelp} from 'actions/index.js';
import {SELECTOR_WAVEFORM} from 'constants/contextual-help.js';
import {setWaveformSelection, setWaveformSelectionEnd} from 'actions/index.js';
import crop from 'Crop.js';
import WaveformCache from 'repos/WaveformCache.js';

window.waveformFillColor = '#130e2e9c';
const colors = new Colors();
let waveformCache = new WaveformCache(325, 50);

class Waveform extends Component {
    constructor(props) {
        super(props);
        this.state = {
            audioUnit: props.audioUnit,
            buffer: props.audioUnit.buffer,
            x: 0,
            isPlaying: false,
            pts: [],
            coloredPoints: [],
        };
        this.dragging = false;

        this.width = props.width || 325;
        this.height = props.height || 50;

        if (this.width !== waveformCache.width ||
            this.height !== waveformCache.height) {
            waveformCache = new WaveformCache(this.width, this.height);
        }

        this.state.pts = [];
        var self = this;
        this.computeWaveformInChunks();
        this.convertSpaceToSeconds.bind(this);
    }

    // since drawing is slow only do it when waveform changes
    shouldComponentUpdate(nextProps, nextState) {
        if (this.props.buffer !== nextProps.buffer ||
           this.props.id !== nextProps.id) {
            this.state.buffer = nextProps.buffer;
            var self = this;
            this.computeWaveformInChunks();
            return true;
        }
        return this.dragging || this.state.isPlaying || this.state.pts !== nextState.pts || this.state.coloredPoints !== nextState.coloredPoints ||
            this.props.slices !== nextProps.slices;
    }

    computeWaveformInChunks(callback) {
        if (this.state.buffer === undefined) {
            return [];
        }
        if (this.state.buffer.duration < 3) {
            waveformCache.getBasicWaveform(this.props.sample.getId(), this.state.buffer, false)
                .then((pts) => {
                    pts = convertPointsToList(pts, 'rmsY');
                    this.setState({pts: pts, coloredPoints: []});
                });
        } else {
            waveformCache.getWaveform(this.props.sample.getId(), this.state.buffer, false)
                .then((pts) => {
                    this.setState({pts: [], coloredPoints: pts});
                });
        }
    }

    stopSound() {
        var audioUnit = this.state.audioUnit;
        audioUnit.stopSound();
        this.setState({
            isPlaying: false
        });
    }

    playSound(self, startOffset) {
        var audioUnit = self.state.audioUnit;
        if (self.state.isPlaying) {
            self.stopSound();
        } else {
            //self.state['x'] = startOffset / 2;
            self.setState(self.state);
            self.state['isPlaying'] = true;
            var startOffsetTime = self.convertSpaceToSeconds(startOffset);
            audioUnit.playSound(startOffsetTime);
            var startTime = new Date();
            this.setState({startOffsetTime: startOffsetTime, startTime: startTime});
        }
    }
   
    convertSpaceToSeconds(x) {
        return this.state.audioUnit.buffer.duration * x / this.width
    }

    convertSecondsToSpace(x) {
        return x / this.state.audioUnit.buffer.duration * this.width
    }

    startDrag(self, evt) {
        document.activeElement.blur();
        evt = evt.evt;
        if (self.draggin) {
            self.dragging = false;
        } else {
            self.playSound(self, evt.offsetX);
            if (self.props.sample !== undefined) {
                let obj =  {
                    start: evt.offsetX,
                    end: evt.offsetX,
                    id: self.props.id || self.props.sample.getId()
                };
                self.props.setWaveformSelection(obj);
            }
            self.dragging = true;
        }


        // figure out which steps were used
        if (this.props.slices !== undefined) {
            let selectedSlice =  this.getSliceFromCoordinate(evt.offsetX);
            this.props.onSelectSlice(selectedSlice);
        }
    }

    getSliceFromCoordinate(x) {
        let sec = this.convertSpaceToSeconds(x);
        let selectedSlices = this.props.slices.filter(s => s.startTime <= sec && s.endTime >= sec);
        return selectedSlices.length == 0 ? undefined : selectedSlices[0];
    }

    moveDrag(self, evt) {
        if (self.dragging) {
            self.props.setWaveformSelectionEnd(evt.evt.layerX);
            if (this.props.sequencerEngine === undefined) {
                return;
            }
            let state = this.props.sequencerEngine.store.getState();
            let startTime = this.convertSpaceToSeconds(state.selector.waveformSelection.start);
            let endTime = this.convertSpaceToSeconds(evt.evt.layerX);
            if (this.props.onChooseSlice !== undefined && startTime < endTime) {
                this.props.onChooseSlice({startTime: startTime, endTime: endTime});
            }
        }
    }

    endDrag(evt) {
        this.dragging = false;
        this.stopSound();
    }

    isCurrentChord(slice) {
        let selected = this.props.selectedChordSample;
        if (this.props.sample === undefined || selected === undefined) {
            return false;
        }

        return this.props.sample.ipfsHash === selected.sampleHash &&
            slice.startTime === selected.startTime &&
            slice.endTime === selected.endTime;
    }
    
    render() {
        var pts = this.state.pts; 
        var self = this;
        let slices = this.props.slices === undefined ? [] : this.props.slices;

        if (this.props.audioUnit === undefined) {
            return (<div>Fetching sample...</div>);
        }

        const color = colors.getColorForHash(this.props.sample.getId());
        return (
            <div>
              <div
                onMouseOver={() => this.props.setContextualHelp(SELECTOR_WAVEFORM)}
                onMouseLeave={() => this.props.setContextualHelp(undefined)}
                className="sound-waveform details-view" >
                <Stage
                  onMouseDown={(evt) => this.startDrag(this, evt)} 
                  onMouseUp={(evt) => this.endDrag(this, evt)} 
                  onMouseMove={(evt) => this.moveDrag(this, evt)}
                  width={isNaN(this.width) ? 1 : this.width} height={this.height}>
                  <Provider store={this.props.store || this.props.sequencerEngine.store}>
                  <Layer>
                    <Rect x={0} y={0} width={isNaN(this.width) ? 1 : this.width} height={this.height} fill={window.waveformFillColor}/>
                    <WaveformSelection height={this.height} sampleId={this.props.id || (this.props.sample !== undefined ? this.props.sample.getId() : 0)}/>
                  </Layer>
                  <Layer>
            {this.state.coloredPoints.length > 0 ?
             this.state.coloredPoints.map( 
                    (pt, i) => 
                     <Rect
                       key={"point-" + i}
                       x={pt.x}
                       y={pt.y}
                       width={pt.width}
                       height={pt.height}
                       fill={'rgb(' + pt.rgb[0] + ',' + pt.rgb[1] + ',' + pt.rgb[2] + ')'}/>) :
                 <Line bezier={true}  points={pts} stroke={color} strokeWidth={2}/>}
                  </Layer>
                  <Layer>
                    <WaveformPlayTick
                      startTime={this.state.startTime}
                      isPlaying={this.state.isPlaying}
                      duration={this.state.audioUnit.buffer && this.state.audioUnit.buffer.duration}
                      startOffsetTime={this.state.startOffsetTime}
                      width={isNaN(this.width) ? 1 : this.width}
                      height={this.height}/>
                  </Layer>
                  </Provider>
                </Stage>
              </div>
            </div>);
    }
}

const mapDispatchToProps = dispatch => {
    return {
        dragSample: sample => dispatch(dragSample(sample)),
        setWaveformSelection: s => dispatch(setWaveformSelection(s)),
        setWaveformSelectionEnd: s => dispatch(setWaveformSelectionEnd(s)),
        setContextualHelp: s => dispatch(setContextualHelp(s)),
    }
};

const mapStateToProps = (state, props) => {
    return {
        selectedChordSample: state.selector.selectedChordSample,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Waveform);

